import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Demos desde video/ → public/guia/:
 * - gif-categoria → paso 2 (catálogo)
 * - gif-compra → paso 4 (carrito / compra; ~8.5 MB, lazy)
 * - gif-pedido → paso 6 (enviar)
 * Pasos 1, 3 y 5 conservan PNG fijas (inicio, producto, checkout).
 */
const STEPS = [
  {
    n: 1,
    title: 'Entra a la tienda',
    text: 'Abre tienda.ohbellezaoficial.com desde el celular o el computador.',
    img: '/guia/01-inicio.png',
    alt: 'Inicio de la tienda Oh Belleza',
    accent: 'rose',
  },
  {
    n: 2,
    title: 'Mira el catálogo',
    text: 'En el menú toca Catálogo (o Uñas). Busca o filtra lo que necesitas.',
    gif: '/guia/gif-categoria.gif',
    alt: 'Demo: cómo navegar el catálogo',
    accent: 'coral',
  },
  {
    n: 3,
    title: 'Elige productos',
    text: 'Abre cada producto, elige cantidad y agrégalo al carrito.',
    img: '/guia/03-producto.png',
    alt: 'Detalle de producto para agregar al carrito',
    accent: 'gold',
  },
  {
    n: 4,
    title: 'Revisa el carrito',
    text: 'Abre el carrito (arriba a la derecha). Confirma cantidades y productos. La demo también muestra el paso a checkout.',
    gif: '/guia/gif-compra.gif',
    alt: 'Demo: carrito y flujo de compra',
    accent: 'teal',
  },
  {
    n: 5,
    title: 'Datos de entrega',
    text: 'Completa nombre, teléfono, ciudad, dirección y, si quieres, transporte y forma de pago.',
    img: '/guia/05-checkout.png',
    alt: 'Formulario de datos de entrega',
    accent: 'blush',
  },
  {
    n: 6,
    title: 'Enviar pedido',
    text: 'Pulsa Enviar pedido. El pedido llega a oficina Oh Belleza para revisarlo.',
    gif: '/guia/gif-pedido.gif',
    alt: 'Demo: enviar el pedido a oficina',
    accent: 'berry',
  },
];

export default function GuiaPedido() {
  const [openStep, setOpenStep] = useState(1);
  /** GIFs pesados: src solo tras abrir el paso (no los 3 a la vez). */
  const [loadedGifs, setLoadedGifs] = useState(() => new Set());

  const toggleStep = (n) => {
    const closing = openStep === n;
    setOpenStep(closing ? null : n);
    if (!closing) {
      const step = STEPS.find((s) => s.n === n);
      if (step?.gif) {
        setLoadedGifs((prev) => {
          if (prev.has(n)) return prev;
          const copy = new Set(prev);
          copy.add(n);
          return copy;
        });
      }
    }
  };

  return (
    <section className="page guia-pedido-page">
      <header className="page-header">
        <h1>Cómo comprar</h1>
        <p className="guia-intro">
          Guía rápida con capturas y demos de la tienda. Toca cada paso; los GIFs se cargan solo al
          abrirlo.
        </p>
      </header>
      <ol className="guia-steps">
        {STEPS.map((s) => {
          const isOpen = openStep === s.n;
          const panelId = `guia-panel-${s.n}`;
          const btnId = `guia-btn-${s.n}`;
          const useGif = Boolean(s.gif);
          const gifLoaded = loadedGifs.has(s.n);
          const mediaSrc = useGif
            ? isOpen || gifLoaded
              ? s.gif
              : undefined
            : s.img;
          const showPlaceholder = useGif && !mediaSrc;

          return (
            <li
              key={s.n}
              className={`guia-step guia-step--${s.accent} guia-step--tilt-${s.n % 2 === 0 ? 'left' : 'right'}${
                isOpen ? ' is-open' : ''
              }`}
            >
              <button
                type="button"
                id={btnId}
                className="guia-step-toggle"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggleStep(s.n)}
              >
                <span className="guia-step-n" aria-hidden="true">
                  {s.n}
                </span>
                <span className="guia-step-title-wrap">
                  <strong className="guia-step-title">{s.title}</strong>
                  <span className="guia-step-hint">
                    {isOpen ? 'Ocultar' : useGif ? 'Ver demo' : 'Ver paso'}
                  </span>
                </span>
                <span className="guia-step-chevron" aria-hidden="true" />
              </button>
              <div
                id={panelId}
                role="region"
                aria-labelledby={btnId}
                className="guia-step-panel"
                aria-hidden={!isOpen}
              >
                <div className="guia-step-panel-inner">
                  <p className="guia-step-text">{s.text}</p>
                  {s.img || s.gif ? (
                    <figure
                      className={`guia-step-figure${useGif ? ' guia-step-figure--gif' : ''}`}
                    >
                      {showPlaceholder ? (
                        <div className="guia-step-gif-placeholder" aria-hidden="true">
                          Toca para cargar demo
                        </div>
                      ) : (
                        <img
                          src={mediaSrc}
                          alt={s.alt || s.title}
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                      {useGif ? (
                        <figcaption className="guia-step-figcaption">Demo animada</figcaption>
                      ) : null}
                    </figure>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
      <p className="guia-cta">
        <Link to="/catalogo" className="btn-primary">
          Ir al catálogo
        </Link>
      </p>
    </section>
  );
}
